(() => {
  const PRODUCT_MAIN_SELECTOR = 'main#MainContent[data-template="product"]';
  const MEDIA_ROOT_SELECTOR = `${PRODUCT_MAIN_SELECTOR} .product-information__media`;
  const INDICATOR_CLASS = 'pdp-thumbnail-indicator';
  const THUMBNAILS_SELECTOR = 'slideshow-controls[thumbnails] .slideshow-controls__thumbnails';
  const THUMBNAILS_CONTAINER_SELECTOR = 'slideshow-controls[thumbnails] .slideshow-controls__thumbnails-container';

  /** @type {WeakMap<HTMLElement, { indicator: HTMLElement; onScroll: () => void; onResize: () => void; observer: MutationObserver }>} */
  const registry = new WeakMap();

  /** @type {boolean} */
  let listenersAttached = false;

  /**
   * The media id the shopper explicitly asked for by clicking a color/option swatch.
   * Used as an authoritative correction target once the gallery finishes refreshing,
   * instead of purely trusting whatever the freshly-fetched markup marks as selected.
   * @type {string | null}
   */
  let pendingMediaId = null;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * @param {HTMLElement} thumbnailsRow
   * @returns {HTMLElement | null}
   */
  function getSelectedThumbnail(thumbnailsRow) {
    return thumbnailsRow.querySelector('.slideshow-control[aria-selected="true"]');
  }

  /**
   * Safety net: guarantees the big/main image always matches whichever thumbnail is
   * marked selected. Some variant-swap code paths can leave the main slideshow out of
   * sync with the thumbnail row (e.g. a media id that hasn't been re-rendered yet), which
   * would otherwise leave the black border pointing at a thumbnail whose image never
   * actually shows in the big container.
   * @param {HTMLElement} mediaRoot
   * @param {HTMLElement} thumbnailsRow
   * @param {HTMLElement | null} selected
   */
  function ensureMainImageMatchesSelection(mediaRoot, thumbnailsRow, selected) {
    if (!selected) return;
    // A swatch click is in progress — don't snap back to stale thumbnail markup.
    if (pendingMediaId) return;

    const thumbnails = Array.from(thumbnailsRow.querySelectorAll('.slideshow-control'));
    const index = thumbnails.indexOf(selected);
    if (index === -1) return;

    const slideshow = mediaRoot.querySelector('slideshow-component');
    if (!slideshow || typeof slideshow.select !== 'function') return;
    if (typeof slideshow.current !== 'number' || slideshow.current === index) return;

    slideshow.select(index, undefined, { animate: !prefersReducedMotion() });
  }

  /**
   * @param {HTMLElement} thumbnailsRow
   * @param {HTMLElement} indicator
   * @param {HTMLElement | null} selected
   * @param {boolean} [instant]
   */
  function moveIndicator(thumbnailsRow, indicator, selected, instant = false) {
    if (!selected) {
      indicator.hidden = true;
      return;
    }

    indicator.hidden = false;
    indicator.style.transition = instant || prefersReducedMotion() ? 'none' : '';

    const left = selected.offsetLeft;
    const width = selected.offsetWidth;
    const height = selected.offsetHeight;

    indicator.style.width = `${width}px`;
    indicator.style.height = `${height}px`;
    indicator.style.transform = `translate3d(${left}px, 0, 0)`;

    if (instant || prefersReducedMotion()) {
      requestAnimationFrame(() => {
        indicator.style.transition = '';
      });
    }
  }

  /**
   * @param {ParentNode | null} mediaRoot
   */
  function syncIndicator(mediaRoot, instant = false) {
    if (!(mediaRoot instanceof HTMLElement)) return;

    const thumbnailsRow = mediaRoot.querySelector(THUMBNAILS_SELECTOR);
    if (!(thumbnailsRow instanceof HTMLElement)) return;

    const state = registry.get(thumbnailsRow);
    if (!state) return;

    const selected = getSelectedThumbnail(thumbnailsRow);
    moveIndicator(thumbnailsRow, state.indicator, selected, instant);
    ensureMainImageMatchesSelection(mediaRoot, thumbnailsRow, selected);
  }

  /**
   * @param {ParentNode | null} mediaRoot
   */
  function teardownIndicator(mediaRoot) {
    if (!(mediaRoot instanceof HTMLElement)) return;

    const thumbnailsRow = mediaRoot.querySelector(THUMBNAILS_SELECTOR);
    if (!(thumbnailsRow instanceof HTMLElement)) return;

    const state = registry.get(thumbnailsRow);
    if (!state) return;

    const container = mediaRoot.querySelector(THUMBNAILS_CONTAINER_SELECTOR);
    container?.removeEventListener('scroll', state.onScroll);
    window.removeEventListener('resize', state.onResize);
    state.observer.disconnect();
    state.indicator.remove();
    registry.delete(thumbnailsRow);
  }

  /**
   * @param {ParentNode | null} mediaRoot
   * @param {{ fadeIn?: boolean }} [options] - When `fadeIn` is true (e.g. after a variant swap
   *   replaces the gallery DOM), the indicator eases its opacity in instead of appearing instantly,
   *   so the reappearance still feels like part of the same smooth transition as a thumbnail click.
   */
  function setupIndicator(mediaRoot, options = {}) {
    if (!(mediaRoot instanceof HTMLElement)) return;

    teardownIndicator(mediaRoot);

    const thumbnailsRow = mediaRoot.querySelector(THUMBNAILS_SELECTOR);
    if (!(thumbnailsRow instanceof HTMLElement)) return;

    const indicator = document.createElement('span');
    indicator.className = INDICATOR_CLASS;
    indicator.setAttribute('aria-hidden', 'true');

    if (options.fadeIn && !prefersReducedMotion()) {
      indicator.setAttribute('data-fade-in', '');
    }

    thumbnailsRow.prepend(indicator);

    const onScroll = () => syncIndicator(mediaRoot, true);
    const onResize = () => syncIndicator(mediaRoot, true);

    const container = mediaRoot.querySelector(THUMBNAILS_CONTAINER_SELECTOR);
    container?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    // Watch the ground-truth `aria-selected` state directly instead of relying solely on
    // the `slideshow:select` event. Some navigation paths (e.g. jumping more than one
    // thumbnail away) update `aria-selected` asynchronously after a DOM reorder trick,
    // which could otherwise leave the sliding border stuck on the wrong thumbnail.
    const observer = new MutationObserver(() => {
      requestAnimationFrame(() => syncIndicator(mediaRoot, false));
    });
    observer.observe(thumbnailsRow, {
      attributes: true,
      attributeFilter: ['aria-selected'],
      subtree: true,
    });

    registry.set(thumbnailsRow, { indicator, onScroll, onResize, observer });

    requestAnimationFrame(() => {
      syncIndicator(mediaRoot, true);

      if (indicator.hasAttribute('data-fade-in')) {
        requestAnimationFrame(() => indicator.removeAttribute('data-fade-in'));
      }
    });
  }

  /**
   * @param {Event} event
   */
  function handleSlideshowSelect(event) {
    if (!(event instanceof CustomEvent) || !event.detail) return;

    const mediaRoot = document.querySelector(MEDIA_ROOT_SELECTOR);
    if (!mediaRoot || !mediaRoot.contains(event.target)) return;

    requestAnimationFrame(() => syncIndicator(mediaRoot, false));
  }

  /**
   * @param {Event} event
   */
  function handleOptionChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    const mediaId = target.dataset.featuredMediaId || target.dataset.optionMediaId;
    if (mediaId) pendingMediaId = mediaId;
  }

  /**
   * After the gallery markup is refreshed for a new variant, explicitly (re)select the
   * media id the shopper actually clicked. Some products don't get their `sorted_media`
   * order corrected server-side (e.g. the variant's image isn't the one picked as
   * "first"), which otherwise leaves the freshly-loaded image sitting unselected in the
   * thumbnail row while the big image/border stay on the old one.
   * @param {HTMLElement} mediaRoot
   */
  function applyPendingMediaSelection(mediaRoot) {
    const mediaId = pendingMediaId;
    if (!mediaId) return;

    let attempts = 0;

    const trySelect = () => {
      // A newer selection request has superseded this one; stop retrying.
      if (pendingMediaId !== mediaId) return;

      const slideshow = mediaRoot.querySelector('slideshow-component');
      const hasSlides = slideshow && 'slides' in slideshow && Array.isArray(slideshow.slides) && slideshow.slides.length > 0;

      if (!slideshow || typeof slideshow.select !== 'function' || !hasSlides) {
        if (++attempts < 12) requestAnimationFrame(trySelect);
        return;
      }

      slideshow.select({ id: mediaId }, undefined, { animate: false });
      pendingMediaId = null;
    };

    trySelect();
  }

  function handleVariantUpdate() {
    const mediaRoot = document.querySelector(MEDIA_ROOT_SELECTOR);
    if (!mediaRoot) return;

    requestAnimationFrame(() => {
      applyPendingMediaSelection(mediaRoot);
      setupIndicator(mediaRoot, { fadeIn: true });
    });
  }

  function init() {
    const productMain = document.querySelector(PRODUCT_MAIN_SELECTOR);
    if (!productMain) return;

    const mediaRoot = document.querySelector(MEDIA_ROOT_SELECTOR);
    setupIndicator(mediaRoot);

    if (listenersAttached) return;

    listenersAttached = true;
    productMain.addEventListener('slideshow:select', handleSlideshowSelect);
    productMain.addEventListener('variant:update', handleVariantUpdate);
    productMain.addEventListener('change', handleOptionChange, { capture: true });
  }

  function boot() {
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
